import React, { createContext, useContext, useEffect, useState } from 'react';
import { api, ApiError } from './api';

export type AdminRole = 'super_admin' | 'admin';

export interface AdminUser {
  id: number;
  name: string;
  email: string;
  role: AdminRole;
}

interface AuthContextValue {
  admin: AdminUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const TOKEN_KEY = 'azura_admin_token';
const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setLoading(false); return; }

    api.get<{ admin: AdminUser }>('/api/auth/me')
      .then(({ admin }) => setAdmin(admin))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setLoading(false));
  }, []);

  const login = async (email: string, password: string) => {
    const { token, admin } = await api.post<{ token: string; admin: AdminUser }>(
      '/api/auth/login', { email, password }, { auth: false }
    );
    localStorage.setItem(TOKEN_KEY, token);
    setAdmin(admin);
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAdmin(null);
  };

  return (
    <AuthContext.Provider value={{ admin, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export { ApiError };
